import { useRef } from 'react';
import { Box, IconButton, InputAdornment, TextField } from '@mui/material';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { getCalendarDatePlaceholder, handleCalendarDateDisplayChange } from '../app/date';

export default function CalendarDateTextField({
  label,
  value,
  onValueChange,
  onBlur,
  disabled,
  fullWidth,
  size = 'small',
  sx,
  inputProps,
  isoValue,
  onIsoChange,
  calendarOpenLabel = 'Calendário',
  ...rest
}) {
  const nativeDateRef = useRef(null);
  const showPicker = typeof onIsoChange === 'function';

  const openNativePicker = () => {
    const el = nativeDateRef.current;
    if (!el || disabled) return;
    try {
      if (typeof el.showPicker === 'function') {
        el.showPicker();
      } else {
        el.click();
      }
    } catch {
      try {
        el.click();
      } catch {
        /* ignore */
      }
    }
  };

  const pickerValue =
    isoValue && typeof isoValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(isoValue.trim())
      ? isoValue.trim()
      : '';

  const textField = (
    <TextField
      {...rest}
      size={size}
      type="text"
      label={label}
      value={value}
      onChange={(e) =>
        handleCalendarDateDisplayChange(e, (syn) => {
          onValueChange(syn.target.value);
        })
      }
      onBlur={onBlur}
      placeholder={getCalendarDatePlaceholder()}
      inputProps={{ inputMode: 'numeric', maxLength: 10, ...inputProps }}
      InputLabelProps={{ shrink: true }}
      disabled={disabled}
      fullWidth={fullWidth}
      sx={showPicker ? { width: fullWidth ? '100%' : undefined } : sx}
      InputProps={
        showPicker
          ? {
              ...rest.InputProps,
              endAdornment: (
                <>
                  {rest.InputProps?.endAdornment}
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      edge="end"
                      disabled={disabled}
                      onClick={openNativePicker}
                      onMouseDown={(e) => e.preventDefault()}
                      aria-label={calendarOpenLabel}
                    >
                      <CalendarTodayIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                </>
              ),
            }
          : rest.InputProps
      }
    />
  );

  if (!showPicker) {
    return textField;
  }

  return (
    <Box
      sx={{
        position: 'relative',
        display: fullWidth ? 'block' : 'inline-flex',
        flexDirection: 'column',
        width: fullWidth ? '100%' : 'auto',
        ...(typeof sx === 'object' && sx != null ? sx : {}),
      }}
    >
      <input
        ref={nativeDateRef}
        type="date"
        value={pickerValue}
        onChange={(e) => {
          const v = e.target.value;
          if (v) onIsoChange(v);
        }}
        tabIndex={-1}
        aria-hidden="true"
        style={{
          position: 'absolute',
          width: 1,
          height: 1,
          padding: 0,
          margin: -1,
          overflow: 'hidden',
          clip: 'rect(0, 0, 0, 0)',
          clipPath: 'inset(50%)',
          whiteSpace: 'nowrap',
          border: 0,
          opacity: 0,
          pointerEvents: 'none',
        }}
      />
      {textField}
    </Box>
  );
}
